import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { LookupItem } from "@/types/admin";

function fmtDate(d?: string | null): string | null {
  if (!d) return null;
  const parts = d.slice(0, 10).split("-"); // YYYY-MM-DD
  if (parts.length !== 3) return null;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/** Seconda riga per le offerte: periodo di validità + prezzo (distingue le omonime). */
function offerSubMeta(item: LookupItem): string | null {
  const out: string[] = [];
  const da = fmtDate(item.validitaDa);
  const a = fmtDate(item.validitaA);
  if (da || a) out.push(`valido ${da ?? "?"}→${a ?? "?"}`);
  if (item.prezzoKwh != null) out.push(`${item.prezzoKwh} €/kWh`);
  else if (item.prezzoSmc != null) out.push(`${item.prezzoSmc} €/Smc`);
  return out.length ? out.join(" · ") : null;
}

export interface AutocompleteProps {
  label: string;
  value: LookupItem | null;
  onChange: (item: LookupItem | null) => void;
  fetchItems: (q: string) => Promise<LookupItem[]>;
  placeholder?: string;
  id: string;
  required?: boolean;
}

export function Autocomplete({
  label,
  value,
  onChange,
  fetchItems,
  placeholder,
  id,
  required,
}: AutocompleteProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LookupItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setQuery(value ? value.name : "");
    }
  }, [value, open]);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      fetchItems(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
  }

  function handleSelect(item: LookupItem) {
    onChange(item);
    setQuery(item.name);
    setOpen(false);
    setResults([]);
  }

  function handleClear() {
    onChange(null);
    setQuery("");
    setOpen(false);
    setResults([]);
  }

  function handleFocus() {
    setOpen(true);
    if (!query) {
      setLoading(true);
      fetchItems("")
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }
  }

  function handleBlur(e: React.FocusEvent) {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setTimeout(() => {
        setOpen(false);
        if (!value || query !== value.name) {
          setQuery(value ? value.name : "");
        }
      }, 150);
    }
  }

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <div ref={containerRef} className="relative">
        <input
          id={id}
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder ?? "Cerca..."}
          autoComplete="off"
          className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 pr-8"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Rimuovi selezione"
          >
            <X size={14} />
          </button>
        )}
        {open && (loading || results.length > 0) && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-md overflow-hidden max-h-52 overflow-y-auto">
            {loading && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Caricamento...
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Nessun risultato
              </div>
            )}
            {results.map((item) => (
              <button
                key={item.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex flex-col"
              >
                <span>{item.name}</span>
                {(item.fornitore || item.tipoCliente || item.tipo) && (
                  <span className="text-xs text-muted-foreground">
                    {[item.fornitore, item.tipoCliente, item.tipo]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
                {offerSubMeta(item) && (
                  <span className="text-xs text-muted-foreground/80 tabular-nums">
                    {offerSubMeta(item)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
